"use client";

import { RiArrowLeftLine } from "@remixicon/react";
import type { FestivalDetails, FestivalEventType, ProcessedSet, Review } from "@workspace/types";
import { Button } from "@workspace/ui/components/button";
import { Separator } from "@workspace/ui/components/separator";
import { useRouter, useSearchParams } from "next/navigation";
import { useMemo } from "react";
import { Exportsetbutton } from "@/components/features/festival/export/export-schedule-button";
import { EventGroupSelector } from "@/components/features/festival/navigation/event-group-selector";
import { FestivalVersionSelector } from "@/components/features/festival/navigation/festival-version-selector";
import { isLineupOnlyFestival } from "@/lib/features/festival/festival-status";

// CalendarData interface for calendar data type
interface CalendarData {
	id: string;
	artist: string;
	stage: string;
	time: string;
	startHour: number;
	duration: number;
	day: string;
	imageUrl?: string | null;
	youtube_url?: string | null;
	status?: {
		isInSchedule: boolean;
		sawLive: boolean;
		sawStream: boolean;
		mustSee: boolean;
	};
}

interface FestivalHeaderProps {
	festivalDetails: FestivalDetails;
	festivalSlug: string;
	selectedDay?: string;
	availableDays: string[];
	calendarData?: CalendarData[];
	processedSets?: ProcessedSet[];
	reviews?: Review[];
	stages?: { id: string; name: string; position: number }[];
	eventType?: "main_festival" | "pre_party" | "after_party";
	onEventGroupChange?: (groupId: string, viewType: 'integrated' | 'show_list') => void;
	availableEventTypes?: FestivalEventType[];
	username?: string;
	userId?: string;
	userProfileImageUrl?: string;
}

export function FestivalHeader({
	festivalDetails,
	festivalSlug,
	selectedDay,
	availableDays,
	calendarData,
	processedSets,
	reviews,
	stages,
	eventType,
	onEventGroupChange,
	availableEventTypes,
	username,
	userId,
	userProfileImageUrl,
}: FestivalHeaderProps) {
	const router = useRouter();
	const searchParams = useSearchParams();
	const eventGroupParam = searchParams?.get('eventGroup') ?? null;

	// Derive available days from calendarData (has overnight-adjusted day values)
	// instead of using availableDays prop (which may have raw calendar days)
	// Uses UTC arithmetic to match groupSetsByDay() in use-festival-data-processing.ts
	const exportAvailableDays = useMemo(() => {
		if (!calendarData || calendarData.length === 0) return availableDays;

		// Get unique days from calendar data (already overnight-adjusted)
		const uniqueDays = Array.from(new Set(calendarData.map(item => item.day)));

		// Sort chronologically using UTC (matches calendar day ordering)
		if (festivalDetails?.start_date && festivalDetails.end_date) {
			const dayNamesToIndex = new Map<string, number>();
			const startDate = new Date(festivalDetails.start_date);
			const endDate = new Date(festivalDetails.end_date);
			const dayNames = ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"];

			for (let i = 0; i < 14; i++) {
				const date = new Date(startDate);
				date.setUTCDate(startDate.getUTCDate() + i);
				const dayName = dayNames[date.getUTCDay()];
				if (dayName && !dayNamesToIndex.has(dayName)) dayNamesToIndex.set(dayName, i);
				if (date >= endDate) break;
			}

			return uniqueDays.sort((a, b) => {
				const indexA = dayNamesToIndex.get(a) ?? 999;
				const indexB = dayNamesToIndex.get(b) ?? 999;
				return indexA - indexB;
			});
		}

		return uniqueDays;
	}, [calendarData, availableDays, festivalDetails]);

	return (
		<div className="flex items-center justify-between gap-2">
			<div className="flex items-center gap-2">
				<Button
					variant="secondary"
					size="sm"
					onClick={() => {
						if (window.history.length > 1) {
							router.back();
						} else {
							router.push("/festivals");
						}
					}}
					className="inline-flex items-center text-sm"
				>
					<RiArrowLeftLine className="mr-1 h-4 w-4" />
					<span>Back</span>
				</Button>
				<Separator className="hidden h-4 sm:block" orientation="vertical" />
				<span className="line-clamp-1 hidden font-medium text-sm sm:block">
					{festivalDetails.name}
				</span>
			</div>
			<div className="flex items-center gap-2">
				<FestivalVersionSelector
					currentFestival={{
						...festivalDetails,
						slug: festivalSlug,
						description: null,
					}}
					basePath={userId ? "username" : "festivals"}
					username={userId ? username : undefined}
					userId={userId}
				/>
				<EventGroupSelector
					onGroupChange={onEventGroupChange}
					availableEventTypes={availableEventTypes}
					festivalDetails={festivalDetails}
				/>
				{!isLineupOnlyFestival(festivalDetails) &&
					selectedDay &&
					calendarData &&
					processedSets &&
					reviews &&
					stages && (
						<Exportsetbutton
							festivalName={festivalDetails.name}
							festivalSlug={festivalSlug}
							festivalId={festivalDetails.id}
							selectedDay={selectedDay}
							availableDays={exportAvailableDays}
							username={username}
							userProfileImageUrl={userProfileImageUrl}
							calendarData={calendarData?.map((item) => ({
								...item,
								status: item.status || {
									isInSchedule: false,
									sawLive: false,
									sawStream: false,
									mustSee: false,
								},
							}))}
							reviews={reviews}
							stages={stages}
							eventType={eventType}
						/>
					)}
			</div>
		</div>
	);
}
